import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import { icons } from "../../../assets";
import "../readable-form.scss";

const STATUS = ["Not Started", "In Progress", "Completed"].map((v) => ({ value: v, label: v }));

// react-select renders its menu inline, so inside .table-responsive (overflow: auto)
// it is clipped at the cell edge and the options can't be seen. Portal it to <body>
// and lift it above the modal's own stacking context.
const menuPortal: any = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
  menuPosition: "fixed",
  styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
};

const fmtDate = (d: any) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "";

// Timeline is picked as years + months + weeks (each defaulting to 0); target date is
// derived from all three.
const num = (n: number) => Array.from({ length: n + 1 }, (_, i) => ({ value: i, label: `${i}` }));
const YEARS = num(5);
const MONTHS = num(11);
const WEEKS = num(3);

const fmtTimeline = (it: any) => {
  const y = it.timelineYears || 0;
  const mo = it.timelineMonths || 0;
  const w = it.timelineWeeks || 0;
  const parts = [y ? `${y} yr` : "", mo ? `${mo} mo` : "", w ? `${w} wk` : ""].filter(Boolean);
  return parts.length ? parts.join(" ") : "-";
};

// yyyy-mm-dd from LOCAL parts (toISOString would shift by a day in +ve timezones)
const localISO = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// base + years + months + weeks, as yyyy-mm-dd. Adds years+months first and clamps
// the day to the last valid day of the target month, so e.g. Jan 31 + 1 month lands on
// Feb 28/29 rather than overflowing into March (native setMonth rolls over).
// base is the Start Date (falls back to today when none is set).
const isoAdd = (base: Date, years: number, months: number, weeks: number) => {
  const anchor = new Date(base.getFullYear() + years, base.getMonth() + months, 1);
  const lastDay = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const d = new Date(anchor.getFullYear(), anchor.getMonth(), Math.min(base.getDate(), lastDay));
  d.setDate(d.getDate() + weeks * 7);
  return localISO(d);
};

// An action past its target date and not yet Completed is overdue — that is what
// triggers the candidate's reassessment, so it has to be visible here.
const isOverdue = (it: any) =>
  !!it.targetDate && it.status !== "Completed" && new Date(it.targetDate) < new Date();

const targetDateCell = (it: any) => {
  if (!it.targetDate) return <span className="muted">-</span>;
  return (
    <span className={isOverdue(it) ? "dp-overdue" : ""} title={isOverdue(it) ? "Past its target date — reassessment due" : ""}>
      {fmtDate(it.targetDate)}
      {isOverdue(it) && " ⚠"}
    </span>
  );
};

const DevelopmentPlanModal = (props: any) => {
  // onChange (optional): lets an embedding page refresh its own counts after an
  // add / edit / delete. Unused when opened as a modal.
  // statusFilter (optional): show only items with this status — the candidate page's
  //   tabs flow items by status (Not Started -> In Progress -> Completed).
  // showForm (default true): the add form. Only Pre-Development shows it.
  const { candidate, onChange, statusFilter, showForm = true } = props;
  const dispatch = useDispatch();

  const today = localISO(new Date());
  const emptyForm = { gap: "", action: "", timelineYears: 0, timelineMonths: 0, timelineWeeks: 0, startDate: today, targetDate: "", owner: "", status: "Not Started" };

  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState<any>({ ...emptyForm });
  const [btnDisable, setBtnDisable] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // when set, the top form is editing this item instead of adding a new one
  const [editingId, setEditingId] = useState<string | null>(null);

  // change any of years/months/weeks -> target date auto-fills from Start Date + duration
  // (still editable after). Falls back to today when no start date is set.
  const applyTimeline = (years: number, months: number, weeks: number) => {
    const total = years + months + weeks;
    setForm((f: any) => ({
      ...f,
      timelineYears: years,
      timelineMonths: months,
      timelineWeeks: weeks,
      targetDate: total > 0 ? isoAdd(f.startDate ? new Date(f.startDate) : new Date(), years, months, weeks) : f.targetDate,
    }));
  };

  // changing the Start Date re-derives the Target Date from it + the current timeline
  const handleStartDate = (val: string) => {
    setForm((f: any) => {
      const total = (f.timelineYears || 0) + (f.timelineMonths || 0) + (f.timelineWeeks || 0);
      return {
        ...f,
        startDate: val,
        targetDate: total > 0 && val ? isoAdd(new Date(val), f.timelineYears, f.timelineMonths, f.timelineWeeks) : f.targetDate,
      };
    });
  };

  const userLogin: any = localStorage.getItem("login");
  const loginId = JSON.parse(userLogin || "{}")?.id;

  const load = () => {
    dispatch(
      actions.getDevelopmentPlansAction(candidate._id, (res: any) => {
        if (res?.data) setItems(res.data);
      })
    );
    if (onChange) onChange();
  };

  useEffect(() => {
    if (candidate?._id) load();
  }, [candidate?._id]);

  const handleAdd = () => {
    if (!form.gap.trim() && !form.action.trim()) {
      showErrorToastMessage("Enter at least a gap or an action.");
      return;
    }
    setBtnDisable(true);
    const payload = {
      successionCandidateRef: candidate._id,
      gap: form.gap,
      action: form.action,
      timelineYears: form.timelineYears,
      timelineMonths: form.timelineMonths,
      timelineWeeks: form.timelineWeeks,
      startDate: form.startDate || null,
      targetDate: form.targetDate || null,
      owner: candidate?.reportingManager || "",
      status: form.status,
      createdBy: loginId,
    };
    dispatch(
      actions.createDevelopmentPlanAction(payload, (res: any) => {
        setBtnDisable(false);
        if (res?.error) {
          const msg = res?.error?.response?.data?.error?.error_description || "Failed to add item";
          showErrorToastMessage(msg);
        } else if (res?.data) {
          showSuccessToastMessage("Development item added");
          setForm({ ...emptyForm });
          load();
        }
      })
    );
  };

  // Load an existing item into the top form and switch it to edit mode.
  const handleEdit = (it: any) => {
    setEditingId(it._id);
    setForm({
      gap: it.gap || "",
      action: it.action || "",
      timelineYears: it.timelineYears || 0,
      timelineMonths: it.timelineMonths || 0,
      timelineWeeks: it.timelineWeeks || 0,
      startDate: it.startDate ? localISO(new Date(it.startDate)) : "",
      targetDate: it.targetDate ? localISO(new Date(it.targetDate)) : "",
      owner: it.owner || "",
      status: it.status || "Not Started",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = () => {
    if (!editingId) return;
    if (!form.gap.trim() && !form.action.trim()) {
      showErrorToastMessage("Enter at least a gap or an action.");
      return;
    }
    setBtnDisable(true);
    const payload = {
      gap: form.gap,
      action: form.action,
      timelineYears: form.timelineYears,
      timelineMonths: form.timelineMonths,
      timelineWeeks: form.timelineWeeks,
      startDate: form.startDate || null,
      targetDate: form.targetDate || null,
      owner: candidate?.reportingManager || "",
      status: form.status,
      updatedBy: loginId,
    };
    dispatch(
      actions.updateDevelopmentPlanAction(editingId, payload, (res: any) => {
        setBtnDisable(false);
        if (res?.error) {
          const msg = res?.error?.response?.data?.error?.error_description || "Failed to update item";
          showErrorToastMessage(msg);
        } else if (res?.data) {
          showSuccessToastMessage("Development item updated");
          setEditingId(null);
          setForm(emptyForm);
          load();
        }
      })
    );
  };

  const handleStatusChange = (item: any, status: string) => {
    dispatch(
      actions.updateDevelopmentPlanAction(item._id, { status, updatedBy: loginId }, (res: any) => {
        if (res?.data) load();
        else showErrorToastMessage("Failed to update status");
      })
    );
  };

  // Confirmed inline on the row rather than in a dialog: this component already IS a
  // modal, and Bootstrap does not support a modal inside a modal.
  const handleRemove = (item: any) => {
    dispatch(
      actions.deleteDevelopmentPlanAction(item._id, (res: any) => {
        setConfirmId(null);
        if (res?.error) showErrorToastMessage("Failed to remove item");
        else {
          showSuccessToastMessage("Item removed");
          load();
        }
      })
    );
  };

  // A tab passes statusFilter to show only its stage's items.
  const visible = statusFilter ? items.filter((it) => it.status === statusFilter) : items;

  return (
    <div className="readable-form dev-plan">
      {/* FORM FIRST — you add here, items appear in the list below. Only the
          Pre-Development tab (and the standalone modal) show it. */}
      {showForm && (
      <div className="dp-add-card">
      <h5 className="dp-add-title">{editingId ? "Edit Development Item" : "Add Development Item"}</h5>
      <div className="row dp-form">
        <div className="col-md-6 mt-2">
          <label className="form-label">Gap Identified</label>
          <input className="form-control" value={form.gap} onChange={(e) => setForm({ ...form, gap: e.target.value })} placeholder="e.g. Leadership" />
        </div>
        <div className="col-md-6 mt-2">
          <label className="form-label">Expectation from Role</label>
          <input className="form-control" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} placeholder="e.g. Lead a team project" />
        </div>
        <div className="col-md-4 mt-3">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            className="form-control"
            value={form.startDate}
            onChange={(e) => handleStartDate(e.target.value)}
            title="When this action begins. The target date is derived from Start Date + the timeline."
          />
          <p className="dp-hint">When the action starts</p>
        </div>
        <div className="col-md-4 mt-3">
          <label className="form-label">Target Date</label>
          <input
            type="date"
            className="form-control"
            value={form.targetDate}
            onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
            title="When this action is due. Once it passes, the candidate is flagged for reassessment."
          />
          <p className="dp-hint">Drives the reassessment reminder</p>
        </div>
        <div className="col-md-4 mt-3">
          <label className="form-label">Timeline</label>
          <div className="dp-timeline">
            <Select
              className="dropdown-select tl-sel"
              options={YEARS}
              value={{ value: form.timelineYears, label: `${form.timelineYears}` }}
              onChange={(s: any) => applyTimeline(s?.value ?? 0, form.timelineMonths, form.timelineWeeks)}
              {...menuPortal}
            />
            <span className="tl-unit">yr</span>
            <Select
              className="dropdown-select tl-sel"
              options={MONTHS}
              value={{ value: form.timelineMonths, label: `${form.timelineMonths}` }}
              onChange={(s: any) => applyTimeline(form.timelineYears, s?.value ?? 0, form.timelineWeeks)}
              {...menuPortal}
            />
            <span className="tl-unit">mo</span>
            <Select
              className="dropdown-select tl-sel"
              options={WEEKS}
              value={{ value: form.timelineWeeks, label: `${form.timelineWeeks}` }}
              onChange={(s: any) => applyTimeline(form.timelineYears, form.timelineMonths, s?.value ?? 0)}
              {...menuPortal}
            />
            <span className="tl-unit">wk</span>
          </div>
          <p className="dp-hint">Sets the target date — you can still edit the date</p>
        </div>
        <div className="col-md-6 mt-2">
          <label className="form-label">Reporting Manager</label>
          <input
            className="form-control"
            value={candidate?.reportingManager || "—"}
            readOnly
            disabled
            title="The employee's current reporting manager (auto-filled, not editable)"
            style={{ backgroundColor: "#e9ecef", color: "#6c757d", cursor: "not-allowed" }}
          />
          <small className="text-muted">Auto — the employee's current reporting manager</small>
        </div>
        <div className="col-md-6 mt-2">
          <label className="form-label">Status</label>
          <Select
            className="dropdown-select"
            options={STATUS}
            value={{ value: form.status, label: form.status }}
            onChange={(s: any) => setForm({ ...form, status: s?.value || "Not Started" })}
            {...menuPortal}
          />
        </div>
      </div>

      <div className="dp-add-actions">
        {editingId ? (
          <>
            <button className="btn add-submit-button buttonsize" disabled={btnDisable} onClick={handleSave}>
              Save Changes
            </button>
            <button className="btn cancel-btn buttonsize" onClick={handleCancelEdit}>
              Cancel
            </button>
          </>
        ) : (
          <button className="btn add-submit-button buttonsize" disabled={btnDisable} onClick={handleAdd}>
            + Add Item
          </button>
        )}
      </div>
      </div>
      )}

      {/* LIST BELOW — headers are ALWAYS rendered, so the empty table itself
          shows that items stack up here. The tab bar labels each stage, so the
          "Development Items" heading is only shown in standalone (modal) mode. */}
      {!statusFilter && (
        <h5 className="mt-2">
          Development Items {items.length > 0 && <span className="count-pill">{items.length}</span>}
        </h5>
      )}
      <div className="table table-responsive">
        <table className="table-main table table-sm">
          <thead className="table-head">
            <tr className="table-head-row">
              <th className="table-head-row">Gap</th>
              <th className="table-head-row">Expectation from Role</th>
              <th className="table-head-row">Start Date</th>
              <th className="table-head-row">Target Date</th>
              <th className="table-head-row">Timeline</th>
              <th className="table-head-row">Reporting Manager</th>
              <th className="table-head-row">Status</th>
              <th className="table-head-row"></th>
            </tr>
            </thead>
            <tbody className="table-body">
              {visible.map((it) => (
                <tr className="table-body-row" key={it._id}>
                  <td>{it.gap || "-"}</td>
                  <td>{it.action || "-"}</td>
                  <td>{it.startDate ? fmtDate(it.startDate) : "-"}</td>
                  <td>{targetDateCell(it)}</td>
                  <td>{fmtTimeline(it)}</td>
                  <td>{candidate?.reportingManager || "-"}</td>
                  <td className="status-cell">
                    <Select
                      className="dropdown-select"
                      options={STATUS}
                      value={{ value: it.status, label: it.status }}
                      onChange={(s: any) => handleStatusChange(it, s?.value)}
                      {...menuPortal}
                    />
                  </td>
                  <td>
                    {confirmId === it._id ? (
                      <span className="inline-confirm">
                        Remove?
                        <button className="ic-yes" onClick={() => handleRemove(it)}>
                          Yes
                        </button>
                        <button className="ic-no" onClick={() => setConfirmId(null)}>
                          No
                        </button>
                      </span>
                    ) : (
                      <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
                        {showForm && (
                          <img
                            title="Edit"
                            src={icons.edit_icon}
                            alt="edit"
                            className="edit-icon"
                            onClick={() => handleEdit(it)}
                          />
                        )}
                        <img
                          title="Remove"
                          src={icons.delete_icon}
                          alt="remove"
                          className="edit-icon"
                          onClick={() => setConfirmId(it._id)}
                        />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            {/* empty: faint placeholder rows show items stack up here — visual, not a sentence */}
            {visible.length === 0 &&
              [0, 1].map((i) => (
                <tr className="table-body-row empty-row" key={`ph-${i}`}>
                  <td colSpan={8}>&nbsp;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    </div>
  );
};

export default DevelopmentPlanModal;
